import { useMemo, useState } from "react";
import type { ProductType } from "@jipraks/shared";
import { ProductCard } from "@client/components/product/ProductCard";
import { ProductTypeFilter } from "@client/components/product/ProductTypeFilter";
import { SectionHeading } from "@client/components/ui/SectionHeading";
import { CardGridSkeleton, ErrorState } from "@client/components/ui/StateMessages";
import { useProducts } from "@client/hooks/use-catalog";
import { catalogCopy } from "@client/locales/id/catalog";

/** Filters client-side: the catalog is small, and one request keeps type switching instant. */
export function CatalogSection() {
  const { data: products, isPending, isError, refetch } = useProducts();
  const [type, setType] = useState<ProductType | undefined>();

  const availableTypes = useMemo(
    () => [...new Set((products ?? []).map((product) => product.type))],
    [products],
  );
  const visible = (products ?? []).filter((product) => !type || product.type === type);

  return (
    <section id="katalog" className="mx-auto w-full max-w-6xl scroll-mt-20 px-4 py-16 sm:px-6 md:py-24">
      <SectionHeading
        eyebrow={catalogCopy.catalogEyebrow}
        title={catalogCopy.catalogTitle}
        description={catalogCopy.catalogDescription}
      />
      <div className="mt-8">
        <ProductTypeFilter value={type} onChange={setType} availableTypes={availableTypes} />
      </div>
      <div className="mt-8">
        {isPending && <CardGridSkeleton />}
        {isError && <ErrorState onRetry={() => void refetch()} />}
        {!isPending && !isError && visible.length === 0 && (
          <p className="text-foreground-muted">{catalogCopy.catalogEmpty}</p>
        )}
        {visible.length > 0 && (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
